import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Form, Button, Spinner, Modal } from 'react-bootstrap';
import { BsPencil, BsTrash, BsGeoAlt, BsImage, BsThreeDotsVertical, BsStarFill, BsToggleOn, BsToggleOff } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import representationService from '../api/representationService';
import { uploadFile } from '../api/uploadService';
import { useSearchParams } from 'react-router-dom';

const EMPTY_FORM = {
  pays: '',
  is_headquarter: false,
  prefix: 'Notre Representation En',
  nom_representant: '',
  telephone_representant_1: '',
  telephone_representant_2: '',
  numeros_representation: ['', '', '', ''],
  secretaire_1_nom: '', secretaire_1_tel_1: '', secretaire_1_tel_2: '',
  secretaire_2_nom: '', secretaire_2_tel_1: '', secretaire_2_tel_2: '',
};

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map((e) => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]{6,20}$/;
function isValidPhone(val) { return !val || PHONE_REGEX.test(val.trim()); }

function mapApiToForm(row) {
  const nums = Array.isArray(row.numeros_representation) ? row.numeros_representation : [];
  while (nums.length < 4) nums.push('');
  return {
    pays: row.pays || '',
    is_headquarter: Boolean(row.is_headquarter),
    prefix: row.prefix !== undefined && row.prefix !== null && row.prefix !== '' ? row.prefix : 'Notre Representation En',
    nom_representant: row.nom_representant || '',
    telephone_representant_1: row.telephone_representant_1 || '',
    telephone_representant_2: row.telephone_representant_2 || '',
    numeros_representation: nums.slice(0, 4),
    secretaire_1_nom: row.secretaire_1_nom || '',
    secretaire_1_tel_1: row.secretaire_1_tel_1 || '',
    secretaire_1_tel_2: row.secretaire_1_tel_2 || '',
    secretaire_2_nom: row.secretaire_2_nom || '',
    secretaire_2_tel_1: row.secretaire_2_tel_1 || '',
    secretaire_2_tel_2: row.secretaire_2_tel_2 || '',
  };
}

export default function RepresentationsPage() {
  const isViewer = useSelector((s) => s.auth.user?.role) === 'viewer';
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list');
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [flagFile, setFlagFile] = useState(null);
  const [flagPreview, setFlagPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [filterPays, setFilterPays] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const add = searchParams.get('add') === 'true';
  const fileRef = useRef();
  const [toggleTarget, setToggleTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => { if (add) setView('add'); }, [add]);

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });
  const hideToast = () => setToast({ message: '', type: 'success' });
  const handleClearFilters = () => { setFilterPays(''); setFilterStatut(''); setPage(1); };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filterPays) params.pays = filterPays;
      if (filterStatut) params.statut = filterStatut;
      const { data: res } = await representationService.list(params);
      setData(res.data);
      setPagination(res.pagination || { total: 0, totalPages: 1, currentPage: 1 });
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setLoading(false); }
  }, [filterPays, filterStatut, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const PHONE_FIELDS = [
    'telephone_representant_1', 'telephone_representant_2',
    'secretaire_1_tel_1', 'secretaire_1_tel_2',
    'secretaire_2_tel_1', 'secretaire_2_tel_2',
  ];

  const setF = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (PHONE_FIELDS.includes(k)) {
      setFormErrors((fe) => ({ ...fe, [k]: v && !isValidPhone(v) ? 'Numéro invalide' : '' }));
    } else {
      setFormErrors((fe) => ({ ...fe, [k]: '' }));
    }
  };

  const setNum = (i, v) => {
    setForm((f) => { const n = [...f.numeros_representation]; n[i] = v; return { ...f, numeros_representation: n }; });
    setFormErrors((fe) => ({ ...fe, [`numeros_representation_${i}`]: v && !isValidPhone(v) ? 'Numéro invalide' : '' }));
  };

  const handleFlagChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFlagFile(file);
    setFlagPreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.pays) errors.pays = 'Pays requis';
    if (!form.nom_representant) errors.nom_representant = 'Nom du représentant requis';
    if (!form.telephone_representant_1) {
      errors.telephone_representant_1 = 'Téléphone requis';
    } else if (!isValidPhone(form.telephone_representant_1)) {
      errors.telephone_representant_1 = 'Numéro invalide';
    }
    ['telephone_representant_2', 'secretaire_1_tel_1', 'secretaire_1_tel_2', 'secretaire_2_tel_1', 'secretaire_2_tel_2'].forEach((f) => {
      if (form[f] && !isValidPhone(form[f])) errors[f] = 'Numéro invalide';
    });
    form.numeros_representation.forEach((n, i) => {
      if (n && !isValidPhone(n)) errors[`numeros_representation_${i}`] = 'Numéro invalide';
    });
    if (view === 'add' && !flagFile) errors.flag = 'Le drapeau (image) est requis';
    return errors;
  };

  const buildPayload = () => {
    const payload = {};
    const add_ = (key, value) => {
      const norm = value === '' ? null : value;
      if (view === 'add') { if (norm !== null) payload[key] = norm; return; }
      const prev = editItem?.[key] === '' ? null : editItem?.[key];
      if (prev !== norm) payload[key] = norm;
    };
    ['pays', 'prefix', 'nom_representant', 'telephone_representant_1', 'telephone_representant_2',
      'secretaire_1_nom', 'secretaire_1_tel_1', 'secretaire_1_tel_2',
      'secretaire_2_nom', 'secretaire_2_tel_1', 'secretaire_2_tel_2'].forEach((k) => add_(k, form[k]));

    payload.is_headquarter = Boolean(form.is_headquarter);

    const nums = form.numeros_representation.filter(Boolean);
    const normNums = nums.length ? nums : null;
    if (view === 'add') { if (normNums) payload.numeros_representation = normNums; }
    else {
      const prev = Array.isArray(editItem?.numeros_representation) ? editItem.numeros_representation : [];
      if (JSON.stringify(prev) !== JSON.stringify(normNums || [])) payload.numeros_representation = normNums;
    }
    return payload;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    setSaving(true); setUploadProgress(0);
    try {
      const payload = buildPayload();
      if (flagFile) {
        const { publicUrl } = await uploadFile('representations', flagFile, (p) => setUploadProgress(p));
        payload.drapeauUrl = publicUrl;
      }
      if (view === 'add') { await representationService.create(payload); showToast('Représentation ajoutée avec succès'); }
      else { await representationService.update(editItem.id, payload); showToast('Représentation mise à jour avec succès'); }
      setView('list'); resetForm(); fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setSaving(false); setUploadProgress(0); }
  };

  const resetForm = () => { setForm(EMPTY_FORM); setEditItem(null); setFlagFile(null); setFlagPreview(null); setFormErrors({}); setUploadProgress(0); };

  const handleEditOpen = (row) => {
    setForm(mapApiToForm(row)); setFlagPreview(row.drapeau_url || null);
    setFlagFile(null); setEditItem(row); setFormErrors({}); setView('edit');
  };

  const handleDelete = async () => {
    try {
      await representationService.remove(deleteTarget.id);
      showToast('Représentation supprimée avec succès');
      setDeleteTarget(null); fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); setDeleteTarget(null); }
  };

  const handleToggleStatus = (row) => {
    setToggleTarget(row);
  };

  const confirmToggleStatus = async () => {
    if (!toggleTarget) return;
    setTogglingId(toggleTarget.id);
    try {
      const newStatus = toggleTarget.statut === 'active' ? 'inactive' : 'active';
      await representationService.update(toggleTarget.id, { statut: newStatus });
      showToast(`Statut mis à jour - Représentation ${newStatus === 'active' ? 'activée' : 'désactivée'} avec succès`);
      setToggleTarget(null);
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setToggleTarget(null);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400, fontSize: 'clamp(13px,2vw,18px)' }}>
          ADMINISTRATIONS DES REPRÉSENTATIONS DANS LE MONDE
        </h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen((v) => !v)}>
          <span className="fw-semibold" style={{ fontSize: 14 }}>Filtres de recherche</span>
          <span>{filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}</span>
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row className="gy-1 gx-3 align-items-end">
            <Col md={4} xs={12}>
              <Form.Label className="small mb-0 fw-semibold">Pays</Form.Label>
              <Form.Control size="sm" value={filterPays} onChange={(e) => { setFilterPays(e.target.value); setPage(1); }} placeholder="Filtrer par pays..." />
            </Col>
            <Col md={4} xs={12}>
              <Form.Label className="small mb-0 fw-semibold">Statut</Form.Label>
              <Form.Select size="sm" value={filterStatut} onChange={(e) => { setFilterStatut(e.target.value); setPage(1); }}>
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </Form.Select>
            </Col>
            <Col md={4} xs={12} className="d-flex gap-2">
              {(filterPays || filterStatut) && (
                <Button onClick={handleClearFilters} variant="outline-secondary" size="sm" className="text-uppercase px-2">EFFACER</Button>
              )}
            </Col>
          </Row>
        </div>
      </div>

      {/* LIST */}
      {view === 'list' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h6 style={{ fontWeight: 700, margin: 0 }}>Résultats ({pagination.total ?? data.length})</h6>
            {!isViewer && (
              <Button size="sm" style={{ backgroundColor: '#8b1512', border: 'none' }}
                onClick={() => { resetForm(); setView('add'); }}>+ Nouvelle Représentation</Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8b1512' }} /></div>
          ) : data.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucune représentation trouvée.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="d-none d-lg-block table-mcm-wrap" style={{ overflowX: 'auto' }}>
                <table className="table-mcm">
                  <thead>
                    <tr>
                      {['Pays', 'Représentant', 'Secrétariat 1', 'Secrétariat 2', 'Centres de prière', 'Statut', ...(isViewer ? [] : ['Actions'])].map((h) => (
                        <th key={h} className="th-mcm">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.id} style={{ verticalAlign: 'top' }}>
                        <td className="td-mcm">
                          {row.drapeau_url && <img src={row.drapeau_url} alt="flag" style={{ width: 28, height: 20, objectFit: 'cover', borderRadius: 2, marginBottom: 4, display: 'block' }} />}
                          {row.prefix && <div style={{ fontSize: 12, color: '#8b1512', fontWeight: 500 }}>{row.prefix}</div>}
                          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {row.pays}
                            {row.is_headquarter && <HeadquartersBadge />}
                          </div>
                        </td>
                        <td className="td-mcm">
                          <div>{row?.nom_representant?.charAt(0)?.toUpperCase() + row?.nom_representant?.slice(1)}</div>
                          <div style={{ color: '#888', fontSize: 12 }}>{row.telephone_representant_1}</div>
                          {row.telephone_representant_2 && <div style={{ color: '#888', fontSize: 12 }}>{row.telephone_representant_2}</div>}
                        </td>
                        <td className="td-mcm">
                          <div>{row.secretaire_1_nom || '—'}</div>
                          {row.secretaire_1_tel_1 && <div style={{ color: '#888', fontSize: 12 }}>{row.secretaire_1_tel_1}</div>}
                          {row.secretaire_1_tel_2 && <div style={{ color: '#888', fontSize: 12 }}>{row.secretaire_1_tel_2}</div>}
                        </td>
                        <td className="td-mcm">
                          <div>{row.secretaire_2_nom || '—'}</div>
                          {row.secretaire_2_tel_1 && <div style={{ color: '#888', fontSize: 12 }}>{row.secretaire_2_tel_1}</div>}
                          {row.secretaire_2_tel_2 && <div style={{ color: '#888', fontSize: 12 }}>{row.secretaire_2_tel_2}</div>}
                        </td>
                        <td className="td-mcm">
                          {(row.prayer_centers || []).length === 0
                            ? <span style={{ color: '#aaa', fontSize: 11 }}>—</span>
                            : (row.prayer_centers || []).map((pc) => (
                              <div key={pc.id} style={{ marginBottom: 6 }}>
                                <div style={{ fontWeight: 500, fontSize: 12 }}>{pc.nom}</div>
                                {pc.lieu && <div style={{ color: '#666', fontSize: 11 }}>{pc.lieu}</div>}
                                {pc.telephone_1 && <div style={{ color: '#888', fontSize: 11 }}>{pc.telephone_1}</div>}
                                {pc.map_link && (
                                  <a href={pc.map_link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#8b1512', display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <BsGeoAlt size={10} /> Carte
                                  </a>
                                )}
                              </div>
                            ))}
                        </td>
                        <td className="td-mcm" style={{ textAlign: 'center' }}>
                          {!isViewer ? (
                            <button
                              onClick={() => handleToggleStatus(row)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title={row.statut === 'active' ? 'Déactiver' : 'Activer'}
                            >
                              {row.statut === 'active' ? (
                                <BsToggleOn size={24} color="#28a745" />
                              ) : (
                                <BsToggleOff size={24} color="#ccc" />
                              )}
                            </button>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, backgroundColor: row.statut === 'active' ? '#d4edda' : '#f8d7da', color: row.statut === 'active' ? '#155724' : '#721c24' }}>
                              {row.statut}
                            </span>
                          )}
                        </td>
                        {!isViewer && (
                          <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>
                            <button className="icon-btn-mcm" onClick={() => handleEditOpen(row)}><BsPencil size={14} color="#555" /></button>
                            <button className="icon-btn-mcm" onClick={() => setDeleteTarget(row)}><BsTrash size={14} color="#8b1512" /></button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="d-lg-none">
                <div className="d-flex flex-column gap-3">
                  {data.map((row) => (
                    <div key={row.id} className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
                      <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: 10 }}>
                        <div>
                          <div className="d-flex align-items-center" style={{ fontWeight: 700, color: '#8b1512', fontSize: 14, gap: 8 }}>
                            {row.drapeau_url && <img src={row.drapeau_url} alt="flag" style={{ width: 22, height: 16, objectFit: 'cover', borderRadius: 2 }} />}
                            <span>{row.pays}</span>
                            {row.is_headquarter && <HeadquartersBadge />}
                            {row.prefix && <span style={{ fontSize: 12, fontWeight: 500, color: '#8b1512', backgroundColor: '#fdf2f2', padding: '1px 6px', borderRadius: 4 }}>{row.prefix}</span>}
                          </div>
                          <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>{row.nom_representant}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {!isViewer && (
                            <button
                              onClick={() => handleToggleStatus(row)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title={row.statut === 'active' ? 'Déactiver' : 'Activer'}
                            >
                              {row.statut === 'active' ? (
                                <BsToggleOn size={28} color="#28a745" />
                              ) : (
                                <BsToggleOff size={28} color="#ccc" />
                              )}
                            </button>
                          )}
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, backgroundColor: row.statut === 'active' ? '#d4edda' : '#f8d7da', color: row.statut === 'active' ? '#155724' : '#721c24' }}>
                            {row.statut}
                          </span>
                          {!isViewer && (
                            <div style={{ position: 'relative' }}>
                              <BsThreeDotsVertical onClick={() => setOpenMenuId(openMenuId === row.id ? null : row.id)} size={18} color="#555" style={{ cursor: 'pointer' }} />
                              {openMenuId === row.id && (
                                <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 100, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', minWidth: 140, overflow: 'hidden', marginTop: 4 }}>
                                  <button type="button" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }} onClick={() => { handleEditOpen(row); setOpenMenuId(null); }}>
                                    <BsPencil size={13} color="#555" /> Modifier
                                  </button>
                                  <button type="button" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, color: '#8b1512', cursor: 'pointer' }} onClick={() => { setDeleteTarget(row); setOpenMenuId(null); }}>
                                    <BsTrash size={13} color="#8b1512" /> Supprimer
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="fw-semibold" style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Représentant</div>
                        <div style={{ fontSize: 13 }}>{row.nom_representant}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{row.telephone_representant_1}</div>
                        {row.telephone_representant_2 && <div style={{ fontSize: 12, color: '#666' }}>{row.telephone_representant_2}</div>}
                      </div>
                      {(row.prayer_centers || []).length > 0 && (
                        <div>
                          <div className="fw-semibold" style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Centres de prière</div>
                          <div className="d-flex flex-wrap gap-2">
                            {(row.prayer_centers || []).map((pc) => (
                              <div key={pc.id} className="bg-light rounded p-2" style={{ flex: '1 1 140px', border: '1px solid #f1f1f1', fontSize: 12 }}>
                                <div style={{ fontWeight: 600 }}>{pc.nom}</div>
                                {pc.lieu && <div style={{ color: '#666' }}>{pc.lieu}</div>}
                                {pc.telephone_1 && <div style={{ color: '#888' }}>{pc.telephone_1}</div>}
                                {pc.map_link && <a href={pc.map_link} target="_blank" rel="noreferrer" style={{ color: '#8b1512', fontSize: 11 }}><BsGeoAlt size={10} /> Carte</a>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <Pagination currentPage={page} totalPages={pagination.totalPages} onPageChange={setPage} />
            </>
          )}
        </>
      )}

      {/* ADD / EDIT FORM */}
      {(view === 'add' || view === 'edit') && (
        <div style={{ margin: '0 auto 24px' }}>
          <h6 className="fw-bold mb-3">{view === 'add' ? 'Ajouter une représentation' : 'Modifier la représentation'}</h6>
          <div className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>

            {/* Main info */}
            <div className="mb-3">
              <FormRow label="Nom du pays :" error={formErrors.pays}>
                <Form.Control value={form.pays} onChange={(e) => setF('pays', e.target.value)} isInvalid={!!formErrors.pays} placeholder="Cameroun" size="sm" />
              </FormRow>
              <FormRow label="Préfixe / Indicatif :">
                <Form.Control value={form.prefix} onChange={(e) => setF('prefix', e.target.value)} placeholder="Notre Representation En" size="sm" />
              </FormRow>
              <FormRow label="Is Headquarters :">
                <Form.Check
                  type="checkbox"
                  id="is-headquarter"
                  label="Cette représentation est le siège principal"
                  checked={form.is_headquarter}
                  onChange={(e) => setF('is_headquarter', e.target.checked)}
                />
              </FormRow>
              <FormRow label="Drapeau :" error={formErrors.flag}>
                <div className="d-flex align-items-center gap-2" style={{ border: `1px solid ${formErrors.flag ? '#dc3545' : '#ccc'}`, borderRadius: 4, padding: '5px 10px' }}>
                  {flagPreview
                    ? <img src={flagPreview} alt="drapeau" style={{ width: 36, height: 24, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                    : <div style={{ width: 36, height: 24, backgroundColor: '#f0f0f0', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><BsImage size={14} color="#aaa" /></div>}
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFlagChange} />
                  <Button variant="outline-secondary" size="sm" type="button" onClick={() => fileRef.current.click()} style={{ fontSize: 12 }}>Choose</Button>
                  {flagFile && <span style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{flagFile.name}</span>}
                </div>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-1"><div style={{ height: 6, backgroundColor: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}><div style={{ height: '100%', backgroundColor: '#8b1512', width: `${uploadProgress}%`, transition: 'width 0.3s' }} /></div></div>
                )}
              </FormRow>
              <FormRow label="Nom du représentant :" error={formErrors.nom_representant}>
                <Form.Control value={form.nom_representant} onChange={(e) => setF('nom_representant', e.target.value)} isInvalid={!!formErrors.nom_representant} placeholder="Frère Jean-Baptiste Kouadio" size="sm" />
              </FormRow>
              <FormRow label="Téléphone :">
                <div className="d-flex gap-2 flex-wrap">
                  <div style={{ flex: '1 1 160px' }}>
                    <Form.Control size="sm" value={form.telephone_representant_1} onChange={(e) => setF('telephone_representant_1', e.target.value)} isInvalid={!!formErrors.telephone_representant_1} placeholder="+225 07 12 34 56 78" />
                    {formErrors.telephone_representant_1 && <div className="text-danger" style={{ fontSize: 11, marginTop: 2 }}>{formErrors.telephone_representant_1}</div>}
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <Form.Control size="sm" value={form.telephone_representant_2} onChange={(e) => setF('telephone_representant_2', e.target.value)} isInvalid={!!formErrors.telephone_representant_2} placeholder="(optionnel)" />
                    {formErrors.telephone_representant_2 && <div className="text-danger" style={{ fontSize: 11, marginTop: 2 }}>{formErrors.telephone_representant_2}</div>}
                  </div>
                </div>
              </FormRow>
              <FormRow label="Numéros de la représentation :">
                <div className="d-flex gap-2 flex-wrap">
                  {form.numeros_representation.map((n, i) => (
                    <div key={i} style={{ flex: '1 1 140px', minWidth: 120 }}>
                      <Form.Control size="sm" value={n} onChange={(e) => setNum(i, e.target.value)} isInvalid={!!formErrors[`numeros_representation_${i}`]} placeholder={`Numéro ${i + 1}`} />
                      {formErrors[`numeros_representation_${i}`] && <div className="text-danger" style={{ fontSize: 11, marginTop: 2 }}>{formErrors[`numeros_representation_${i}`]}</div>}
                    </div>
                  ))}
                </div>
              </FormRow>
            </div>

            {/* Secrétariat */}
            <div className="rounded p-3 mb-3" style={{ border: '1px solid #eee' }}>
              <h6 className="fw-bold mb-3" style={{ fontSize: 14 }}>Sécrétariat</h6>
              {[1, 2].map((n) => (
                <div key={n} className="mb-3" style={{ paddingBottom: n === 1 ? 10 : 0, borderBottom: n === 1 ? '1px solid #f5f5f5' : 'none' }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Secrétaire {n}</div>
                  <FormRow label="Nom :">
                    <Form.Control size="sm" value={form[`secretaire_${n}_nom`]} onChange={(e) => setF(`secretaire_${n}_nom`, e.target.value)} placeholder={`Secrétaire ${n}`} />
                  </FormRow>
                  <FormRow label="Téléphone :">
                    <div className="d-flex gap-2 flex-wrap">
                      {[1, 2].map((t) => (
                        <div key={t} style={{ flex: '1 1 160px' }}>
                          <Form.Control size="sm" value={form[`secretaire_${n}_tel_${t}`]} onChange={(e) => setF(`secretaire_${n}_tel_${t}`, e.target.value)} isInvalid={!!formErrors[`secretaire_${n}_tel_${t}`]} placeholder={`Tél ${t}`} />
                          {formErrors[`secretaire_${n}_tel_${t}`] && <div className="text-danger" style={{ fontSize: 11, marginTop: 2 }}>{formErrors[`secretaire_${n}_tel_${t}`]}</div>}
                        </div>
                      ))}
                    </div>
                  </FormRow>
                </div>
              ))}
            </div>

            <div className="text-muted mb-3" style={{ fontSize: 12, padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: 4, border: '1px solid #e9ecef' }}>
              💡 Les Espaces Sainte Faustine et Atmosphères de la Victoire se gèrent maintenant dans la section <strong>Centres de prière</strong>.
            </div>

            <div className="text-center mt-4 d-flex gap-3 justify-content-center">
              <Button size="sm" variant="outline-secondary" type="button" style={{ letterSpacing: 1, paddingInline: 24 }} onClick={() => { setView('list'); resetForm(); }}>ANNULER</Button>
              <Button size="sm" type="button" disabled={saving} onClick={handleSave} style={{ backgroundColor: '#8b1512', border: 'none', letterSpacing: 1, paddingInline: 40 }}>
                {saving ? <Spinner size="sm" animation="border" /> : 'ENREGISTRER'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer cette représentation ?</h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>Cette action est irréversible.</p>
          </div>
          {deleteTarget && (
            <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
              <div className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {deleteTarget.drapeau_url && <img src={deleteTarget.drapeau_url} alt="flag" style={{ width: 28, height: 20, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />}
                <span className="fw-semibold" style={{ fontSize: 14 }}>{deleteTarget.pays} {deleteTarget.prefix && <span style={{ fontWeight: 'normal', color: '#8b1512' }}>({deleteTarget.prefix})</span>}</span>
              </div>
              <div className="mb-2"><span className="fw-semibold">Représentant : </span>{deleteTarget.nom_representant || '—'}</div>
              {deleteTarget.telephone_representant_1 && <div><span className="fw-semibold">Tél. : </span>{deleteTarget.telephone_representant_1}</div>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => setDeleteTarget(null)} style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}>ANNULER</button>
            <button onClick={handleDelete} style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13 }}>SUPPRIMER</button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Toggle Status Modal */}
      <Modal show={!!toggleTarget} onHide={() => setToggleTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: toggleTarget?.statut === 'active' ? '#fff3cd' : '#d1ecf1', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              {toggleTarget?.statut === 'active' ? (
                <BsToggleOn size={24} color="#856404" />
              ) : (
                <BsToggleOff size={24} color="#0c5460" />
              )}
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>
              {toggleTarget?.statut === 'active' ? 'Désactiver cette représentation ?' : 'Activer cette représentation ?'}
            </h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
              {toggleTarget?.statut === 'active'
                ? 'La représentation sera marquée comme inactif'
                : 'La représentation sera marquée comme actif'}
            </p>
          </div>
          {toggleTarget && (
            <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
              <div className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {toggleTarget.drapeau_url && <img src={toggleTarget.drapeau_url} alt="flag" style={{ width: 28, height: 20, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />}
                <span className="fw-semibold" style={{ fontSize: 14 }}>{toggleTarget.pays} {toggleTarget.prefix && <span style={{ fontWeight: 'normal', color: '#8b1512' }}>({toggleTarget.prefix})</span>}</span>
              </div>
              <div className="mb-2"><span className="fw-semibold">Représentant : </span>{toggleTarget.nom_representant || '—'}</div>
              {toggleTarget.telephone_representant_1 && <div><span className="fw-semibold">Tél. : </span>{toggleTarget.telephone_representant_1}</div>}
              <div className="mt-2"><span className="fw-semibold">Statut actuel : </span>
                <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, backgroundColor: toggleTarget.statut === 'active' ? '#d4edda' : '#f8d7da', color: toggleTarget.statut === 'active' ? '#155724' : '#721c24', marginLeft: 6 }}>
                  {toggleTarget.statut}
                </span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => setToggleTarget(null)} disabled={togglingId === toggleTarget?.id} style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}>ANNULER</button>
            <button onClick={confirmToggleStatus} disabled={togglingId === toggleTarget?.id} style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: toggleTarget?.statut === 'active' ? '#ffc107' : '#28a745', color: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              {togglingId === toggleTarget?.id ? (
                <>
                  <Spinner size="sm" animation="border" /> Mise à jour...
                </>
              ) : (
                toggleTarget?.statut === 'active' ? 'DÉSACTIVER' : 'ACTIVER'
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

function FormRow({ label, error, children }) {
  return (
    <div className="mb-3">
      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>{label}</div>
      <div>{children}</div>
      {error && <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function HeadquartersBadge() {
  return (
    // <span title="Siège principal" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 10, backgroundColor: '#fff3cd', color: '#856404', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
    //   <BsStarFill size={9} /> HQ
    // </span>
    <span className="headquarters-badge">SIÈGE</span>
  );
}
